import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiMail, FiSearch, FiUsers } from "react-icons/fi";
import Card from "../components/ui/Card";
import Avatar from "../components/ui/Avatar";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import { employeesApi, DirectoryContact } from "../api/employees";
import toast from "react-hot-toast";

const AnnuairePage: React.FC = () => {
  const [contacts, setContacts] = useState<DirectoryContact[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesApi.directory({
        search: debouncedSearch || undefined,
        department: department || undefined,
        per_page: 200,
      });
      setContacts(res.data.data?.contacts ?? []);
      setDepartments(res.data.data?.departments ?? []);
    } catch {
      toast.error("Impossible de charger l'annuaire");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, department]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const grouped = useMemo(() => {
    const map = new Map<string, DirectoryContact[]>();
    contacts.forEach((contact) => {
      const key = contact.department?.trim() || "Autres";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(contact);
    });
    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, "fr"),
    );
  }, [contacts]);

  const departmentOptions = [
    { value: "", label: "Tous les départements" },
    ...departments.map((d) => ({ value: d, label: d })),
  ];

  return (
    <div style={{ padding: "2rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1
          style={{
            margin: "0 0 0.35rem",
            fontSize: "var(--font-size-2xl)",
            fontWeight: 700,
            color: "var(--color-primary)",
          }}
        >
          Annuaire
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
          }}
        >
          Retrouvez vos collègues : consultation en lecture seule
        </p>
      </div>

      <Card padding="1.25rem" style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 220px",
            gap: "0.75rem",
          }}
        >
          <div style={{ position: "relative" }}>
            <FiSearch
              size={16}
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--color-text-muted)",
                pointerEvents: "none",
              }}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, poste, service…"
              style={{ paddingLeft: "2.25rem" }}
            />
          </div>
          <Select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            options={departmentOptions}
          />
        </div>
        {!loading && (
          <p
            style={{
              margin: "0.75rem 0 0",
              fontSize: "var(--font-size-xs)",
              color: "var(--color-text-muted)",
            }}
          >
            {contacts.length} collaborateur{contacts.length > 1 ? "s" : ""}{" "}
            affiché{contacts.length > 1 ? "s" : ""}
          </p>
        )}
      </Card>

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "3rem" }}
        >
          <LoadingSpinner size={36} />
        </div>
      ) : contacts.length === 0 ? (
        <Card padding="2rem">
          <EmptyState
            icon={<FiUsers size={32} />}
            title="Aucun contact trouvé"
            description="Modifiez votre recherche ou contactez le service RH si l'annuaire est vide."
          />
        </Card>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          {grouped.map(([dept, deptContacts]) => (
            <div key={dept}>
              <h2
                style={{
                  margin: "0 0 0.75rem",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 600,
                  color: "var(--color-text-secondary)",
                }}
              >
                {dept}
                <span
                  style={{
                    fontWeight: 400,
                    color: "var(--color-text-muted)",
                    marginLeft: "0.35rem",
                  }}
                >
                  ({deptContacts.length})
                </span>
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {deptContacts.map((contact) => (
                  <Card key={contact.id} padding="1rem">
                    <div
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        alignItems: "flex-start",
                      }}
                    >
                      <Avatar name={contact.full_name} size={44} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 600,
                            fontSize: "var(--font-size-sm)",
                          }}
                        >
                          {contact.full_name}
                        </p>
                        {contact.position && (
                          <p
                            style={{
                              margin: "0.15rem 0 0",
                              fontSize: "var(--font-size-xs)",
                              color: "var(--color-text-secondary)",
                            }}
                          >
                            {contact.position}
                          </p>
                        )}
                        {contact.manager_name && (
                          <p
                            style={{
                              margin: "0.2rem 0 0",
                              fontSize: "0.65rem",
                              color: "var(--color-text-muted)",
                            }}
                          >
                            Manager : {contact.manager_name}
                          </p>
                        )}
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.3rem",
                              marginTop: "0.5rem",
                              fontSize: "var(--font-size-xs)",
                              color: "var(--color-brand)",
                              textDecoration: "none",
                            }}
                          >
                            <FiMail size={12} />
                            {contact.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p
        style={{
          marginTop: "1.25rem",
          fontSize: "var(--font-size-xs)",
          color: "var(--color-text-muted)",
          lineHeight: 1.6,
        }}
      >
        Seules les informations professionnelles sont affichées. Pour toute mise
        à jour, contactez le service RH.
      </p>
    </div>
  );
};

export default AnnuairePage;
